import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasicProtectedRoute } from "./components/auth/protected-route";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { PublicLayout } from "./components/layout/PublicLayout";
import CustomerPage from "./pages/customer/page";
import CustomerProfilePage from "./pages/customer/profile-page";
import Dashboard from "./pages/dashboard/page";
import RecentEmails from "./pages/email/page";
import { AuthInitializer } from "./pages/login/callback";
import Login from "./pages/login/page";
import CustomerLanding from "./pages/my-customer/CustomerLanding";
import Reports from "./pages/reports/page";
import SecureVolumePage from "./pages/secure-volume/page";
import { UserRoleType } from "./store/services/userService";

import { useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import ActivityRouteDialog from "./components/activity-route-dialog/ActivityRouteDialog";
import CreateSuspectUrlHandler from "./components/create-suspect/CreateSuspectUrlHandler";
import BaseLayout from "./components/layout/BaseLayout";
import { ScrollToTop } from "./components/layout/ScrollToTop";
import WebLayout from "./components/layout/WebLayout";
import ActivityPage from "./pages/activity-list/page";
import WorksPage from "./pages/works/page";
import CalendarPage from "./pages/calender/page";
import CustomerTimelinePage from "./pages/customer-timeline/page";
import CustomerGroupsPage from "./pages/customer/groups-page";
import MyOpportunitiesPage from "./pages/my-opportunites/page";
import OpportunitiesPage from "./pages/opportunites/page";
import MyContactsPage from "./pages/my-contacts/page";
import ProspectSuspectPage from "./pages/prospect-suspect/page";
import SalesManagerPage from "./pages/sales-manager/page";
import SettingsPage from "./pages/settings/page";
import UnauthorizedPage from "./pages/unauthorized/page";
import UserActivityPage from "./pages/user-activity-page/page";
import { selectIsAuthenticated } from "./store/slices/authSlice";

// Hook för att hantera bakgrundsläge för dialogrutor
const useBackgroundLocation = () => {
  const location = useLocation();

  // Kontrollera om det finns ett sparat bakgrundsläge
  const state = location.state as { background?: Location };

  // Returnera antingen bakgrundsläget eller det nuvarande läget
  return state?.background || location;
};

export function AppRoutes() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm")); // Use 'md' breakpoint
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const location = useLocation();

  // Hämta bakgrundsläget
  const backgroundLocation = useBackgroundLocation();

  // Avgör vilket layout som ska användas baserat på skärmstorlek
  const AppLayout = isDesktop ? WebLayout : BaseLayout;

  return (
    <>
      <ScrollToTop />
      {/* Huvudroutes - använder bakgrundsläge när dialogruta är öppen */}
      <Routes location={backgroundLocation}>
        {/* Public routes */}
        <Route element={<PublicLayout />}>
          <Route path="/login/callback" element={<AuthInitializer />} />
          <Route path="/login" element={<Login />} />
        </Route>

        {/* Protected routes */}
        <Route
          element={
            <BasicProtectedRoute>
              <AppLayout />
            </BasicProtectedRoute>
          }
        >
          <Route
            path="/users/:userId/activity"
            element={
              <ProtectedRoute>
                <UserActivityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <CustomerLanding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-contacts"
            element={
              <ProtectedRoute>
                <MyContactsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prospect-suspect"
            element={
              <ProtectedRoute>
                <ProspectSuspectPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute
                allowedRoles={[
                  UserRoleType.Admin,
                  UserRoleType.CrmAdmin,
                  UserRoleType.SalesAdmin,
                  UserRoleType.SalesPerson,
                  UserRoleType.ExternalSale,
                  UserRoleType.ExternalSaleAdmin,
                ]}
              >
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activities"
            element={
              <ProtectedRoute>
                <ActivityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/works"
            element={
              <ProtectedRoute>
                <WorksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/:id"
            element={
              <ProtectedRoute>
                <CustomerPage />
              </ProtectedRoute>
            }
          >
            <Route index element={<CustomerProfilePage />} />
            <Route path="groups" element={<CustomerGroupsPage />} />
            {/* Här kan du lägga till fler sub-routes, t.ex: */}
            {/* <Route path="timeline" element={<CustomerTimeline />} /> */}
            {/* <Route path="activities" element={<CustomerActivities />} /> */}
          </Route>
          <Route
            path="/customer/:customerNo/timeline"
            element={
              <ProtectedRoute>
                <CustomerTimelinePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emails"
            element={
              <ProtectedRoute>
                <RecentEmails />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UsersPage />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/my/opportunities"
            element={
              <ProtectedRoute>
                <MyOpportunitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/opportunites"
            element={
              <ProtectedRoute>
                <OpportunitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute
                allowedRoles={[
                  UserRoleType.Admin,
                  UserRoleType.CrmAdmin,
                  UserRoleType.AppAdmin,
                ]}
              >
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/secure-volume"
            element={
              <ProtectedRoute>
                <SecureVolumePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-manager"
            element={
              <ProtectedRoute
                allowedRoles={[
                  UserRoleType.SuperAdmin,
                  UserRoleType.CrmAdmin,
                  UserRoleType.SalesAdmin,
                  UserRoleType.ExternalSaleAdmin,
                ]}
              >
                <SalesManagerPage />
              </ProtectedRoute>
            }
          />
          {/* Lägg till route för direktnavigering till aktiviteter */}
          <Route
            path="/activity/:id"
            element={
              <ProtectedRoute>
                <div>
                  <h2>Aktivitetsdetaljer</h2>
                  <p>
                    Du ser denna sida när du navigerar direkt till en
                    aktivitets-URL.
                  </p>
                  <p>
                    Normalt sett skulle du se ett detaljerat aktivitetsformulär
                    här.
                  </p>
                </div>
              </ProtectedRoute>
            }
          />
          {/* Lägg till route för skapande av suspects via URL */}
          <Route
            path="/customer/create-suspect"
            element={
              <ProtectedRoute>
                <div>
                  <h2>Skapa ny kundsuspekt</h2>
                  <p>
                    Du ser denna sida när du navigerar direkt till en
                    create-suspect URL.
                  </p>
                  <p>
                    Normalt sett skulle du se ett formulär för att skapa en
                    suspect här.
                  </p>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/unauthorized"
            element={
              <ProtectedRoute>
                <UnauthorizedPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>

        {/* Default redirect */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>

      {/* 
        Dialog-routes - dessa renderas ovanpå den nuvarande sidan när vi har
        ett bakgrundsläge (dvs. när en dialogruta öppnades från en sida)
      */}
      {backgroundLocation !== location && (
        <Routes>
          {/* Route för att visa en befintlig aktivitet */}
          <Route path="/activity/:id" element={<ActivityRouteDialog />} />
          <Route
            path="/customer/create-suspect"
            element={<CreateSuspectUrlHandler />}
          />
        </Routes>
      )}
    </>
  );
}
